import React, { useState, useEffect, useRef } from 'react';

/**
 * AnimatedMetric Component
 * 
 * Displays metrics with count-up animation triggered on scroll.
 * Designed for premium engineering aesthetic - subtle, professional, no bounce.
 * 
 * Props:
 * - value: string (e.g., "<50ms", "99.9%", "1M+")
 * - label: string (e.g., "Latency", "Uptime")
 * - duration: number (animation duration in ms, default 1200)
 * - showNote: boolean (show "Measured in production" note)
 */

export function AnimatedMetric({ value, label, duration = 1200, showNote = false, className = '' }) {
  const [displayValue, setDisplayValue] = useState('0');
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  // Parse the value to extract number, prefix, and suffix
  const parseValue = (val) => {
    if (!val) return { number: 0, prefix: '', suffix: '', decimals: 0 };
    
    const str = String(val);
    
    // Match patterns like "<50ms", "99.9%", "1M+", "10K+", "3x", "€50M+", "$100K"
    const match = str.match(/^([<>€$£¥]?)(\d+\.?\d*)([a-zA-Z%+x€$£¥]*)/);
    
    if (!match) return { number: 0, prefix: '', suffix: str, decimals: 0 };
    
    const prefix = match[1] || '';
    const numberStr = match[2] || '0';
    const suffix = match[3] || '';
    const decimals = numberStr.includes('.') ? numberStr.split('.')[1].length : 0;
    
    return {
      number: parseFloat(numberStr),
      prefix,
      suffix,
      decimals
    };
  };

  const { number, prefix, suffix, decimals } = parseValue(value);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateValue();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateValue = () => {
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out cubic for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = number * easeOut;
      
      // Format the number
      let formatted;
      if (decimals > 0) {
        formatted = currentValue.toFixed(decimals);
      } else {
        formatted = Math.round(currentValue).toString();
      }
      
      setDisplayValue(`${prefix}${formatted}${suffix}`);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  return (
    <div ref={ref} className={`text-center ${className}`}>
      <div 
        className="text-3xl md:text-4xl font-semibold tracking-tight mb-2"
        style={{ 
          color: '#1C7C7D',
          fontFamily: 'JetBrains Mono, monospace',
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {hasAnimated ? displayValue : `${prefix}0${suffix}`}
      </div>
      {label && (
        <div 
          className="text-xs uppercase tracking-wider"
          style={{ color: 'var(--text-muted)', letterSpacing: '0.1em' }}
        >
          {label}
        </div>
      )}
      {showNote && hasAnimated && (
        <div 
          className="text-[10px] mt-2 opacity-0 animate-fade-in"
          style={{ 
            color: 'var(--text-subtle)',
            animation: 'fadeIn 0.5s ease-out 0.3s forwards'
          }}
        >
          Measured in production
        </div>
      )}
    </div>
  );
}

/**
 * MetricsStrip Component
 * 
 * Displays a row of animated metrics with consistent styling.
 */
export function MetricsStrip({ metrics, showNotes = false }) {
  return (
    <div 
      className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 py-8"
    >
      {metrics.map((metric, index) => (
        <AnimatedMetric
          key={index}
          value={metric.value}
          label={metric.label}
          duration={1000 + (index * 150)} // Staggered animation
          showNote={showNotes && index === 0}
        />
      ))}
    </div>
  );
}

export default AnimatedMetric;
